import { StrKey } from '@stellar/stellar-sdk';

import { ENV_PRODUCTION, ENV_TESTNET } from 'constants/env';
import { CONTRACTS } from 'constants/soroban';

import { getEnv } from 'helpers/env';

import { Environment } from 'types/env';
import { EnabledIceDelegationConfig, RawIceDelegationConfig } from 'types/ice-delegation';

export const FRACTION_DEN = 10_000_000;
export const MAX_DELEGATES = 2;

// Enable Phase 1 only after all three deployed proxy identities are verified.
// Disabled networks continue to use Classic delegation and balances.
export const ICE_DELEGATION_CONFIG: Record<Environment, RawIceDelegationConfig> = {
    [ENV_PRODUCTION]: {
        enabled: false,
        proxies: { ice: '', upvote: '', govern: '' },
    },
    [ENV_TESTNET]: {
        enabled: true,
        proxies: {
            ice: 'CASSMR4PXHFA6JLEUCULKLOY4BXGPMTGEYSLLDGF6EQCZOGVNRLF5YTY',
            upvote: 'CAACTYVX4DRXV7HDZ4XGIVMBUQV5VVAWEFNL5YBH7QRUAVGIG4SDOB3K',
            govern: 'CAV4FLWRQ4I4Q2LBY4QIPL6HNJFFNLLQNBYSKA5FAILZYG4K3FDFNN7D',
        },
    },
};

const assertContractId = (value: string, label: string): void => {
    if (!value || !StrKey.isValidContract(value)) {
        throw new Error(`Contract delegation configuration has an invalid ${label}.`);
    }
};

export const validateIceDelegationConfig = (
    config: RawIceDelegationConfig,
    contracts: Pick<(typeof CONTRACTS)[Environment], 'batch' | 'tokenTimelock'>,
): EnabledIceDelegationConfig | null => {
    if (config.enabled !== true) {
        return null;
    }

    assertContractId(config.proxies.ice, 'ICE proxy');
    assertContractId(config.proxies.upvote, 'upvoteICE proxy');
    assertContractId(config.proxies.govern, 'governICE proxy');
    assertContractId(contracts.batch, 'batch contract');
    assertContractId(contracts.tokenTimelock, 'timelock contract');

    const identities = [
        config.proxies.ice,
        config.proxies.upvote,
        config.proxies.govern,
        contracts.batch,
        contracts.tokenTimelock,
    ];
    if (new Set(identities).size !== identities.length) {
        throw new Error('Contract delegation identities must be distinct.');
    }

    return {
        ...config,
        enabled: true,
        batch: contracts.batch,
        tokenTimelock: contracts.tokenTimelock,
    };
};

export const isContractDelegationEnabled = (): boolean =>
    ICE_DELEGATION_CONFIG[getEnv()].enabled === true;

export const getIceDelegationConfig = (): EnabledIceDelegationConfig => {
    const env = getEnv();
    const config = validateIceDelegationConfig(ICE_DELEGATION_CONFIG[env], CONTRACTS[env]);

    if (!config) {
        throw new Error('Contract delegation is not enabled.');
    }

    return config;
};
